import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ComputerDesktopIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { THEMA_KEUZES, useThema, type ThemaKeuze as Keuze } from '../thema';

interface ThemaKeuzeProps {
  /**
   * `dark` staat op een donkere ondergrond (de voettekst van de spelerspagina),
   * `light` op een witte kaart (instellingen).
   */
  variant?: 'dark' | 'light';
  className?: string;
}

const ICONEN: Record<Keuze, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  systeem: ComputerDesktopIcon,
  licht: SunIcon,
  donker: MoonIcon,
};

/**
 * Themakeuze als klein segmented control, gebouwd naar het voorbeeld van
 * LanguageSwitcher.
 *
 * Staat net als de taalkeuze ook op de publieke spelerspagina: die route heeft geen
 * inlog, dus een ouder die de deel-link toegestuurd krijgt kan het nergens anders
 * omzetten. De keuze wordt per apparaat in localStorage bewaard en overleeft dus een
 * herlaadbeurt, maar reist niet mee naar een ander toestel.
 *
 * Iconen in plaats van tekst tot `sm:`. Drie labels naast elkaar passen niet in de
 * breedte die de taalkeuze hiernaast heeft, en op 320px duwen ze de uitleg ernaast weg.
 */
export const ThemaKeuze: React.FC<ThemaKeuzeProps> = ({ variant = 'light', className = '' }) => {
  const { t } = useTranslation('common');
  const { keuze, kies } = useThema();

  return (
    <div
      role="group"
      aria-label={t('theme.label')}
      className={clsx(
        'inline-flex overflow-hidden rounded-md border',
        variant === 'dark' ? 'border-white/25' : 'border-gray-300',
        className,
      )}
    >
      {THEMA_KEUZES.map((optie) => {
        const isActief = keuze === optie;
        const Icoon = ICONEN[optie];

        return (
          <button
            key={optie}
            type="button"
            onClick={() => kies(optie)}
            aria-pressed={isActief}
            title={t('theme.switchTo', { theme: t(`theme.${optie}`) })}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1 text-xs font-medium transition-colors',
              isActief && 'bg-hockey-blue text-white',
              !isActief && variant === 'dark' && 'text-white/75 hover:bg-white/10',
              !isActief && variant === 'light' && 'text-gray-600 hover:bg-gray-100',
            )}
          >
            <Icoon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{t(`theme.${optie}Short`)}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ThemaKeuze;
